"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MathElement = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const nano_theme_1 = require("nano-theme");
const slate_1 = require("slate");
const slate_react_1 = require("slate-react");
const More_1 = require("@jsonjoy.com/ui/lib/icons/interactive/More");
const Popup_1 = require("@jsonjoy.com/ui/lib/4-card/Popup");
const BasicButton_1 = require("@jsonjoy.com/ui/lib/2-inline-block/BasicButton");
const context_1 = require("@jsonjoy.com/ui/lib/styles/context");
const Paper_1 = require("@jsonjoy.com/ui/lib/4-card/Paper");
const context_2 = require("../../../context");
const mathlive_1 = require("../../../void/math/mathlive");
const StripBarHandle_1 = require("../StripBarHandle");
const VoidSelectionOverlay_1 = require("../VoidSelectionOverlay");
const MathOptionsPopup_1 = require("./MathOptionsPopup");
const settings = tslib_1.__importStar(require("./settings"));
const use_t_1 = require("use-t");
const blockClass = (0, nano_theme_1.rule)({
    pos: 'relative',
    mr: '8px 0',
    bdrad: '6px',
    bxz: 'border-box',
});
const stripWrapClass = (0, nano_theme_1.rule)({
    mrt: '4px',
    mrb: '2px',
});
const equationWrapClass = (0, nano_theme_1.rule)({
    pos: 'relative',
    d: 'flex',
    fld: 'column',
    ai: 'center',
    jc: 'center',
    gap: '6px',
    bxz: 'border-box',
});
const fieldWrapClass = (0, nano_theme_1.rule)({
    pos: 'relative',
    d: 'flex',
    jc: 'center',
    ai: 'center',
    w: '100%',
    pd: 0,
    bdrad: '6px',
    bxz: 'border-box',
});
const captionClass = (0, nano_theme_1.rule)({
    textAlign: 'center',
    fz: '13px',
    lh: 1.4,
    us: 'none',
});
const placeholderClass = (0, nano_theme_1.rule)({
    fz: '13px',
    lh: 1.4,
    us: 'none',
});
const moreWrapClass = (0, nano_theme_1.rule)({
    pos: 'absolute',
    t: '-8px',
    insetInlineEnd: '8px',
    trs: 'opacity 0.15s ease',
    z: 1,
});
const MathElement = ({ attributes, children, element }) => {
    const [t] = (0, use_t_1.useT)();
    const mutxt = (0, context_2.useMuTxt)();
    mutxt.things.version.use();
    const styles = (0, context_1.useStyles)();
    const readOnly = (0, slate_react_1.useReadOnly)();
    const isSelected = (0, slate_react_1.useSelected)();
    const isFocused = (0, slate_react_1.useFocused)();
    const selected = isSelected && isFocused;
    const editor = (0, slate_react_1.useSlateStatic)();
    const [hovered, setHovered] = React.useState(false);
    const thingId = element['@thing'];
    const thing = thingId ? mutxt.things.get(thingId) : undefined;
    const tex = thing?.val ?? '';
    const size = settings.getMathSize(element.size);
    const [editing, setEditing] = React.useState(false);
    const [draft, setDraft] = React.useState(tex);
    const fieldRef = React.useRef(null);
    const wrapperRef = React.useRef(null);
    const skipBlurCommitRef = React.useRef(false);
    const enterEdit = React.useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        if (readOnly)
            return;
        if (!thingId)
            return;
        setDraft(tex);
        setEditing(true);
    }, [readOnly, tex, thingId]);
    // biome-ignore lint/correctness/useExhaustiveDependencies: sync field only when toggling edit mode
    React.useEffect(() => {
        if (!editing)
            return;
        const el = fieldRef.current;
        if (el && typeof el.value === 'string' && el.value !== draft) {
            el.value = draft;
        }
        const raf = requestAnimationFrame(() => {
            try {
                fieldRef.current?.focus?.();
            }
            catch { }
        });
        return () => cancelAnimationFrame(raf);
    }, [editing]);
    const commitDraft = React.useCallback((next) => {
        if (!thingId)
            return;
        const trimmed = next.trim();
        const currentThing = mutxt.things.get(thingId);
        const currentTex = currentThing?.val ?? '';
        if (trimmed === currentTex)
            return;
        mutxt.things.update(thingId, { val: trimmed });
        mutxt.sync(false);
    }, [mutxt, thingId]);
    // Slate-react attaches `beforeinput` and friends to the native DOM, so
    // synthetic React handlers on <math-field> would not fire (our wrapper
    // stops the native event before it reaches React's delegation root). Do
    // all interactive handling via native listeners on the field itself.
    React.useEffect(() => {
        if (!editing)
            return;
        const field = fieldRef.current;
        const wrapper = wrapperRef.current;
        if (!field || !wrapper)
            return;
        const focusStillInMath = () => {
            if (typeof field.hasFocus === 'function') {
                try {
                    if (field.hasFocus())
                        return true;
                }
                catch { }
            }
            const ae = document.activeElement;
            if (!ae)
                return false;
            if (ae === field)
                return true;
            if (ae.tagName === 'MATH-FIELD')
                return true;
            if (typeof ae.closest === 'function') {
                if (ae.closest('math-field'))
                    return true;
                if (ae.closest('[class*="ML__"]'))
                    return true;
            }
            return false;
        };
        const onFieldInput = () => {
            const next = field.value ?? '';
            setDraft(next);
        };
        const onFieldBlur = () => {
            if (skipBlurCommitRef.current) {
                skipBlurCommitRef.current = false;
                return;
            }
            setTimeout(() => {
                if (focusStillInMath())
                    return;
                const next = field.value ?? '';
                commitDraft(next);
                setEditing(false);
            }, 0);
        };
        const onFieldKeyDown = (event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                skipBlurCommitRef.current = true;
                const next = field.value ?? '';
                commitDraft(next);
                setEditing(false);
                return;
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                skipBlurCommitRef.current = true;
                setEditing(false);
            }
        };
        field.addEventListener('input', onFieldInput);
        field.addEventListener('blur', onFieldBlur);
        field.addEventListener('keydown', onFieldKeyDown);
        const stop = (event) => event.stopPropagation();
        const bubblingEvents = [
            'beforeinput',
            'input',
            'keydown',
            'keyup',
            'keypress',
            'compositionstart',
            'compositionupdate',
            'compositionend',
            'copy',
            'cut',
            'paste',
            'drop',
            'dragstart',
        ];
        for (const ev of bubblingEvents)
            wrapper.addEventListener(ev, stop);
        return () => {
            field.removeEventListener('input', onFieldInput);
            field.removeEventListener('blur', onFieldBlur);
            field.removeEventListener('keydown', onFieldKeyDown);
            for (const ev of bubblingEvents)
                wrapper.removeEventListener(ev, stop);
        };
    }, [editing, commitDraft]);
    const stopReact = React.useCallback((event) => {
        event.stopPropagation();
    }, []);
    const preventMouseDown = React.useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);
    const selectBlock = React.useCallback(() => {
        try {
            const path = slate_react_1.ReactEditor.findPath(editor, element);
            slate_1.Transforms.select(editor, path);
            slate_react_1.ReactEditor.focus(editor);
        }
        catch { }
    }, [editor, element]);
    const isDark = !styles.light;
    const fieldColor = styles.g(0.15);
    const fieldBg = isDark ? styles.g(1, 0.04) : styles.g(0, 0.04);
    const fieldBorder = isDark ? styles.g(1, 0.1) : styles.g(0, 0.08);
    const captionColor = styles.g(0.42);
    const padding = settings.MATH_SIZE_PADDING[size];
    const spanMode = settings.mathSizeToSpanMode(size);
    const fieldDefaultMode = settings.mathSizeToFieldDefaultMode(size);
    const sizeFontSize = settings.MATH_SIZE_FONT[size];
    const stylePrefix = settings.mathSizeToLatexStylePrefix(size);
    const displayedTex = stylePrefix && tex ? stylePrefix + tex : tex;
    const showToolbar = !readOnly && (selected || hovered) && !editing;
    const showStrip = !readOnly && (hovered || selected) && !editing;
    return (React.createElement("div", { ...attributes, className: blockClass, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) },
        React.createElement("div", { contentEditable: false },
            React.createElement("div", { className: stripWrapClass },
                React.createElement(StripBarHandle_1.StripBarHandle, { ariaLabel: t('Select equation'), onActivate: () => selectBlock(), visible: showStrip, width: "11%", color: isDark ? styles.g(1, 0.32) : styles.g(0, 0.22), colorHover: isDark ? styles.g(1, 0.55) : styles.g(0, 0.45) })),
            React.createElement(Paper_1.Paper, { noOutline: true, style: {
                    margin: '4px 0',
                    position: 'relative',
                    background: selected && !editing ? styles.g(0, 0.05) : 'transparent',
                } },
                React.createElement("div", { className: equationWrapClass, onClick: editing ? undefined : enterEdit, style: { padding, cursor: editing ? 'text' : 'default', fontSize: sizeFontSize } },
                    editing ? (
                    // biome-ignore lint/a11y/useKeyWithClickEvents: math-field manages its own keyboard handling
                    React.createElement("div", { ref: wrapperRef, className: fieldWrapClass, style: {
                            background: fieldBg,
                            border: `1px solid ${fieldBorder}`,
                            colorScheme: isDark ? 'dark' : 'light',
                        }, onMouseDown: stopReact, onClick: stopReact }, React.createElement('math-field', {
                        ref: fieldRef,
                        'default-mode': fieldDefaultMode,
                        style: {
                            maxWidth: '100%',
                            minWidth: '120px',
                            boxSizing: 'border-box',
                            padding: 0,
                            color: fieldColor,
                            background: 'transparent',
                            ['--primary']: fieldColor,
                            ['--text-font-family']: 'inherit',
                            ['--caret-color']: fieldColor,
                            ['--keycap-background']: styles.g(1, 0.08),
                            ['--keycap-background-hover']: styles.g(1, 0.16),
                            ['--keycap-text']: fieldColor,
                            ['--keyboard-background']: styles.g(1, 0.04),
                            ['--keyboard-text']: fieldColor,
                            ['--smart-fence-color']: fieldColor,
                        },
                    }))) : tex ? (React.createElement(mathlive_1.MathSpan, { tex: displayedTex, mode: spanMode, selected: selected, focused: selected, dark: isDark })) : (React.createElement("div", { className: placeholderClass, style: { color: styles.g(0.5) } }, t('Click to enter equation'))),
                    element.caption ? (React.createElement("div", { className: captionClass, style: { color: captionColor } }, element.caption)) : null,
                    !editing && React.createElement(VoidSelectionOverlay_1.VoidSelectionOverlay, { selected: selected })))),
        !readOnly && (React.createElement("div", { contentEditable: false, className: moreWrapClass, onMouseDown: preventMouseDown, style: { opacity: showToolbar ? 1 : 0, pointerEvents: showToolbar ? 'auto' : 'none' } },
            React.createElement(Popup_1.Popup, { renderContext: () => React.createElement(MathOptionsPopup_1.MathOptionsPopup, { element: element }) },
                React.createElement(BasicButton_1.BasicButton, { type: "button", width: 32, height: 32, rounder: true, border: true, onMouseDown: preventMouseDown },
                    React.createElement(More_1.More, { size: 32 }))))),
        children));
};
exports.MathElement = MathElement;
